import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles
} from '@material-ui/core/styles'
import DialogContentText from '@material-ui/core/DialogContentText'
import Typography from '@material-ui/core/Typography'
import { formatDate } from '../../../utils/dates'
import CircularProgress from '@material-ui/core/CircularProgress'
import { UserFacingError } from '../../../errors/UserFacingError'
import { logError } from '../../../logging'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { NetworkError } from '../../../errors/NetworkError'
import { IIncident } from '../../../models/Incident'
import { IUserCapabilities } from '../../../models/User'
import { IImageStorage } from '../../../data_sources/imagestorage/imageStorage'
import { AssigneeType } from '../../../types'
import {
  getSiteFranchiseeId,
  IFranchisee,
  ISite
} from '../../../models/Franchise'
import { IChecklist, IPhoto } from '../../../models/Checklist'
import CheckboxControl from '../../form_controls/CheckboxControl'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'
import { getIncidentFields } from '../../../utils/incident/fields'
import { submitIncident } from '../../../utils/incident/submitIncident'
import { TemplateType, TemplateTypes } from '../../../models/Template'

const styles = (_theme: Theme) =>
  createStyles({
    incidentFields: {
      paddingTop: 8
    },
    readOnlyField: {
      marginTop: 10
    }
  })

export interface ISubmitIncidentDialogState {
  stage:
    | 'confirm'
    | 'submitting'
    | 'submit_success'
    | 'friendly_error'
    | 'unexpected_error'
  errorText?: string
  incidentId: number
  toEmail: boolean
}

export interface ISubmitIncidentDialogProps
  extends WithStyles<typeof styles>,
    IOneplaceLibraryContextProp {
  isOpen: boolean
  capabilities: IUserCapabilities
  incident: IIncident
  imageStorage: IImageStorage
  assigneeType: AssigneeType
  franchisee?: IFranchisee
  site?: ISite
  attachments: IPhoto[]
  incidentChangeTracker?: any
  onClose: () => void
  showEmailDialog: () => void
  onIncidentSubmitted: (incidentId: number) => Promise<void>
  onSubmitDialogsCompleted: () => void
  navigateToDashboard: () => void
  api?: any
  templateType?: TemplateType
  isExistingIncident?: boolean
}

// Use an intermedite component to make sure dialog is unmounted when finished with
export const SubmitIncidentDialog = withStyles(styles)(
  withOneplaceLibraryContext((props: ISubmitIncidentDialogProps) => {
    if (props.isOpen) {
      return <SubmitIncidentDialogContent {...props} />
    } else {
      return null
    }
  })
)

class SubmitIncidentDialogContent extends React.Component<
  ISubmitIncidentDialogProps,
  ISubmitIncidentDialogState
> {
  constructor(props: ISubmitIncidentDialogProps) {
    super(props)
    this.onEmailOptionChanged = this.onEmailOptionChanged.bind(this)
    this.onSubmitClicked = this.onSubmitClicked.bind(this)
    this.onClose = this.onClose.bind(this)
    this.state = {
      stage: 'confirm',
      incidentId: 0,
      toEmail: false
    }
  }

  onEmailOptionChanged(field: 'toEmail', value: any) {
    this.setState({ [field]: !!value })
  }

  async onSubmitClicked() {
    const franchiseeId =
      this.props.assigneeType == 'franchisee'
        ? this.props.franchisee!.id
        : getSiteFranchiseeId(this.props.site!)
    this.setState({ stage: 'submitting' })
    try {
      let incidentId
      if (this.props.templateType === TemplateTypes.INCIDENT) {
        const saveIncidentV2Response =
          await this.props.ctx.client.saveIncidentV2(
            this.props.capabilities.franchiseId,
            this.props.incident as unknown as IChecklist,
            this.props.templateType,
            this.props.isExistingIncident
          )
        incidentId = saveIncidentV2Response.incidentId
      } else {
        incidentId = await submitIncident(
          this.props.ctx.client,
          this.props.capabilities,
          franchiseeId,
          this.props.incident,
          this.props.incidentChangeTracker,
          this.props.attachments,
          this.props.imageStorage
        )
      }
      await this.props.onIncidentSubmitted(incidentId)
      if (this.state.toEmail) {
        this.props.showEmailDialog()
        this.props.onClose()
      } else {
        this.setState({ stage: 'submit_success', incidentId })
      }
    } catch (e) {
      console.log('SubmitIncidentDialog -> onSubmitClicked -> catch: ', e)
      if (e instanceof UserFacingError) {
        this.setState({ stage: 'friendly_error', errorText: e.message })
      } else if (e instanceof NetworkError) {
        this.setState({
          stage: 'unexpected_error',
          errorText: this.props.ctx.i18next.t('incident_network_error')
        })
      } else {
        logError(e, 'Unexpected error while submitting incident')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
        this.setState({ stage: 'unexpected_error', errorText: e.message })
      }
    }
  }

  onClose() {
    if (this.state.stage == 'submit_success') {
      this.props.onClose()
      this.props.onSubmitDialogsCompleted()
    } else {
      this.props.onClose()
    }
  }

  render() {
    const t = this.props.ctx.i18next.t
    const incident = this.props.incident
    const assigneeTypeLabel =
      this.props.assigneeType == 'franchisee'
        ? t('customLabel_franchisee')
        : t('customLabel_site')
    const assigneeName =
      this.props.assigneeType == 'franchisee'
        ? this.props.franchisee!.name
        : this.props.site!.name
    const incidentReportDate = incident.date
      ? formatDate(
          'display_date',
          incident.date!,
          this.props.capabilities.dateFormat
        )
      : ''

    const dismissDisabled = this.state.stage == 'submitting'
    const fields = getIncidentFields(
      this.props.capabilities.dateFormat,
      this.props.capabilities.dateTimeFormat,
      t
    )

    return (
      <Dialog
        open={this.props.isOpen}
        onClose={this.onClose}
        disableBackdropClick={dismissDisabled}
        disableEscapeKeyDown={dismissDisabled}
      >
        <DialogTitle>{t('submit_incident')}</DialogTitle>
        <DialogContent>
          {this.state.stage == 'confirm' && (
            <div>
              <DialogContentText>
                {t('submit_incident_intro')}
              </DialogContentText>
              <div className={this.props.classes.incidentFields}>
                <div className={this.props.classes.readOnlyField}>
                  <Typography variant='caption'>{assigneeTypeLabel}</Typography>
                  <Typography variant='subtitle1'>{assigneeName}</Typography>
                </div>
                <div className={this.props.classes.readOnlyField}>
                  <Typography variant='caption'>
                    {fields.detailFields[0].fields[0].label}
                  </Typography>
                  <Typography variant='subtitle1'>{incident.name}</Typography>
                </div>
                <div className={this.props.classes.readOnlyField}>
                  <Typography variant='caption'>
                    {fields.detailFields[1].fields[3].label}
                  </Typography>
                  <Typography variant='subtitle1'>
                    {incidentReportDate}
                  </Typography>
                </div>
                {(incident.incidentTypePermission.allowSendIncidentEmail ||
                  this.props.capabilities.superuser) && (
                  <div style={{ marginTop: 16 }}>
                    <div>
                      <FormControlLabel
                        control={
                          <CheckboxControl
                            id='toEmailField'
                            field={
                              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                              {
                                name: 'toEmail',
                                value: this.state.toEmail
                              } as any
                            }
                            form={
                              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                              {
                                setFieldValue: this.onEmailOptionChanged
                              } as any
                            }
                            meta={{
                              value: null,
                              touched: false,
                              initialTouched: false
                            }}
                          />
                        }
                        label={t('incident_toemail')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {this.state.stage == 'submitting' && (
            <div style={{ display: 'flex', marginTop: 10 }}>
              <CircularProgress size={32} />
              <Typography
                style={{ fontSize: 16, marginTop: 6, marginLeft: 16 }}
              >
                {t('submitting')}
              </Typography>
            </div>
          )}
          {this.state.stage == 'submit_success' && (
            <div>
              <DialogContentText>
                {t('incident_submit_successful')}
              </DialogContentText>
            </div>
          )}
          {this.state.stage == 'friendly_error' && (
            <div>
              <DialogContentText>
                {t('incident_submit_failed')}
              </DialogContentText>
              <Typography style={{ marginTop: 16 }}>
                {this.state.errorText}
              </Typography>
            </div>
          )}
          {this.state.stage == 'unexpected_error' && (
            <div>
              <DialogContentText>
                {t('incident_submit_error')}
              </DialogContentText>
              <Typography style={{ marginTop: 16 }}>
                {this.state.errorText}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {this.state.stage == 'confirm' && (
            <>
              <Button onClick={this.onClose} color='primary'>
                {t('cancel')}
              </Button>
              <Button onClick={this.onSubmitClicked} color='primary'>
                {t('submit')}
              </Button>
            </>
          )}
          {['friendly_error', 'unexpected_error'].includes(
            this.state.stage
          ) && (
            <>
              <Button onClick={this.onSubmitClicked} color='primary'>
                {t('retry_submission')}
              </Button>
              <Button onClick={this.onClose} color='primary'>
                {t('continue_editing')}
              </Button>
              <Button onClick={this.props.navigateToDashboard} color='primary'>
                {t('go_to_dashboard')}
              </Button>
            </>
          )}
          {this.state.stage == 'submit_success' && (
            <>
              <Button onClick={this.onClose} color='primary'>
                {t('continue_editing')}
              </Button>
              <Button onClick={this.props.navigateToDashboard} color='primary'>
                {t('go_to_dashboard')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    )
  }
}
